<template>
  <div
    class="accordion"
    :class="{
      'accordion--open': isOpen,
      'accordion--nested': nested,
      'accordion--static': isStatic,
    }">
    <!-- Trigger -->
    <component
      :is="isStatic ? 'div' : 'button'"
      class="accordion__trigger"
      :aria-expanded="isStatic ? undefined : isOpen"
      @click="isStatic ? undefined : toggle()">
      <span
        v-if="$slots.prefix"
        class="accordion__prefix">
        <slot name="prefix" />
      </span>
      <span class="accordion__title">{{ title }}</span>
      <!-- Slot optionnel pour contenu additionnel dans le header (ex: compteur, icône) -->
      <span
        v-if="$slots.header"
        class="accordion__header-extra">
        <slot name="header" />
      </span>
      <svg
        v-if="!isStatic"
        class="accordion__chevron"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true">
        <path
          d="M4 6l4 4 4-4"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
      </svg>
    </component>

    <!-- Contenu -->
    <Transition name="accordion-body">
      <div
        v-show="isOpen"
        class="accordion__body">
        <div class="accordion__inner">
          <slot />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

// ── Types ───────────────────────────────────────────────────
interface Props {
  /** Titre affiché dans le trigger */
  title: string
  /** Ouvert par défaut */
  defaultOpen?: boolean
  /**
   * Contrôle externe optionnel.
   * Si fourni, le composant devient "controlled" et émet 'update:open'.
   */
  open?: boolean | null
  /** Style imbriqué — fond légèrement différent du parent */
  nested?: boolean
  /**
   * Mode statique — toujours ouvert, pas de chevron, trigger non cliquable.
   * Utile pour les sections qui ne doivent jamais se replier.
   */
  static?: boolean
  /** Declenchement computed de l'accordion si true */
  eager?: boolean
}

// ── Props ────────────────────────────────────────────────────
const props = withDefaults(defineProps<Props>(), {
  defaultOpen: false,
  open: null,
  nested: false,
  static: false,
  eager: false,
})

// ── Emits ────────────────────────────────────────────────────
const emit = defineEmits<{
  'update:open': [value: boolean]
  toggle: [value: boolean]
}>()

// ── Computed ─────────────────────────────────────────────────
const isStatic = computed(() => props.static)

// ── État interne (uncontrolled par défaut) ─────────────────
// En mode static, on force toujours ouvert
const internalOpen = ref(props.static || props.defaultOpen)

const isOpen = computed(() =>
  props.static ? true : props.open !== null ? props.open : internalOpen.value,
)

// ── Methods ──────────────────────────────────────────────────
function toggle(): void {
  if (props.open !== null) {
    // Mode controlled : on émet, le parent gère
    emit('update:open', !props.open)
  } else {
    internalOpen.value = !internalOpen.value
  }
  emit('toggle', isOpen.value)
}
</script>

<style scoped>
/* ── Wrapper ──────────────────────────────────────────────── */
.accordion {
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: border-color var(--duration-fast) var(--ease-out);
}

.accordion--open {
  border-color: var(--border-subtle);
}

/* ── Trigger ──────────────────────────────────────────────── */
.accordion__trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: var(--space-3);
  transition: background var(--duration-fast) var(--ease-out);
}

.accordion__trigger:hover {
  background: rgba(255, 255, 255, 0.03);
}

.accordion__title {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
}

/* ── Chevron ──────────────────────────────────────────────── */
.accordion__chevron {
  flex-shrink: 0;
  color: var(--text-muted);
  transition:
    transform var(--duration-base) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}

.accordion--open .accordion__chevron {
  transform: rotate(180deg);
  color: var(--accent-blue);
}

/* ── Body ─────────────────────────────────────────────────── */
.accordion__body {
  overflow: hidden;
  margin-top: var(--space-4);
}

.accordion__inner {
  padding: 0 var(--space-4) var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.accordion__prefix {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.accordion--static .accordion__trigger {
  cursor: default;
}

.accordion--static .accordion__trigger:hover {
  background: none;
}

/* Slot header extra (compteur, icône...) */
.accordion__header-extra {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
}

/* ── Variante imbriquée ───────────────────────────────────── */
.accordion--nested {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.07);
}

.accordion--nested .accordion__trigger {
  padding: var(--space-3) var(--space-4);
}

.accordion--nested .accordion__title {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-secondary);
}

.accordion--nested.accordion--open .accordion__title {
  color: var(--text-primary);
}

.accordion--nested .accordion__inner {
  padding: 0 var(--space-3) var(--space-4);
}

@media (max-width: 640px) {
  .accordion__inner {
    padding: 0 var(--space-3) var(--space-4);
  }

  .accordion--nested .accordion__inner {
    padding: 0 var(--space-3) var(--space-3);
  }
}

/* ── Animations ───────────────────────────────────────────── */
.accordion-body-enter-active,
.accordion-body-leave-active {
  transition:
    max-height var(--duration-base) var(--ease-out),
    opacity var(--duration-base) var(--ease-out);
  max-height: 1200px;
  overflow: hidden;
}

.accordion-body-enter-from,
.accordion-body-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
